import type { BlitzPage } from "@blitzjs/next"
import { Box, Button, Stack, TextField, Typography } from "@mui/material"
import Dialog from "@mui/material/Dialog"
import DialogActions from "@mui/material/DialogActions"
import DialogContent from "@mui/material/DialogContent"
import DialogContentText from "@mui/material/DialogContentText"
import DialogTitle from "@mui/material/DialogTitle"
import Grid from "@mui/material/Unstable_Grid2"
import { getAuth } from "firebase/auth"
import { useRouter } from "next/router"
import { useSnackbar } from "notistack"
import { useState, useDebugValue, useEffect } from "react"
import { BusinessCard } from "app/businesses/components/BusinessCard"
import { BusinessNewCard } from "app/businesses/components/BusinessNewCard"
import { AccountButton } from "app/components/AccountButton"
import { AddButton } from "app/components/AddButton"
import { ErrorDisplay } from "app/components/ErrorDisplay"
import { HomeAppBar } from "app/components/HomeAppBar"
import { HomeLogin } from "app/components/HomeLogin"
import  RootLoading from "app/loading"
import {
  useApproveInviteRequestMutation,
  useJoinByInviteLinkMutation,
  useViewerBusinessesQuery,
  useViewerUserQuery,
} from "interface/__generated__/react"
import { t } from "lib/i18n"
import InviteLinkInfo from "pages/inviteLinkInfo"
import {
  getFromLocalStorage,
  removeItemFromLocalStorage,
  storeInLocalStorage,
} from "util/localStorageUtil"

const RootPage: BlitzPage = () => {
  // Environment variables available in process.env
  const router = useRouter()

  // パスから招待コードを呪録
  const invitationCode = Array.isArray(router.query.invitationCode)
    ? router.query.invitationCode[0]
    : router.query.invitationCode ?? null

  let inviteRequestId = Array.isArray(router.query.inviteRequest)
    ? router.query.inviteRequest[0]
    : router.query.inviteRequest ?? null

  if (!inviteRequestId) {
    inviteRequestId = getFromLocalStorage("inviteRequestId")
  }

  useDebugValue("invitationCode")

  const checkApproveInviteRequest = () => {
    const currentUser = getAuth().currentUser
    const isLogin = currentUser != null

    if (inviteRequestId && isLogin && !joinLoading) {
      // メール招待の場合もNameダイアログを表示する
      setOpen(true)
    }
  }

  useEffect(() => {
    if (invitationCode) {
      storeInLocalStorage("invitationCode", invitationCode)
    }
    if (inviteRequestId) {
      storeInLocalStorage("inviteRequestId", inviteRequestId)
      checkApproveInviteRequest()
    }
  }, [inviteRequestId])

  const [approveInviteRequest, { loading: joinLoading, error: joinError }] =
    useApproveInviteRequestMutation()

  const [open, setOpen] = useState(false)
  const [name, setName] = useState("")
  const { enqueueSnackbar } = useSnackbar()

  const handleJoin = () => {
    setOpen(true)
  }

  const handleClose = () => {
    // Remove invitationCode query parameter
    router.push(router.pathname, undefined, { shallow: true })
  }

  const handleDialogClose = () => {
    setOpen(false)
  }

  const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setName(event.target.value)
  }

  const handleDialogSubmit = async () => {
    try {
      if (inviteRequestId) {
        // メール招待: approveInviteRequest に name を渡す
        await approveInviteRequest({
          variables: { input: { inviteRequestId, name } },
        })
        removeItemFromLocalStorage("inviteRequestId")
        router.push(router.pathname, undefined, { shallow: true })
        refetchviewerBusinesses()
        enqueueSnackbar(t("Join successfully"), { variant: "success" })
      } else {
        // QRコード招待: joinByInviteLink
        await joinByInviteLink({
          variables: {
            input: {
              userId: viewer?.viewer?.user.id ?? "",
              name: name,
              inviteLinkCode: invitationCode ?? "",
            },
          },
        })
        router.push(router.pathname, undefined, { shallow: true })
        refetchviewerBusinesses()
        enqueueSnackbar(t("Request successfully"), { variant: "success" })
      }
    } catch (error) {
      if (error instanceof Error) {
        enqueueSnackbar(error.message, { variant: "error" })
      } else {
        enqueueSnackbar(t("Failed to login"), { variant: "error" })
      }
    }
    setOpen(false)
  }

  // useJoinByInviteLinkMutation
  const [joinByInviteLink, { loading: isLoading }] =
    useJoinByInviteLinkMutation()

  const { data: viewer, error: viewerUserError } = useViewerUserQuery()

  const {
    data: viewerBusiness,
    error: viewerBusinessesError,
    refetch: refetchviewerBusinesses,
    loading: viewerBusinessesLoading,
  } = useViewerBusinessesQuery({
    variables: { withDetails: false },
  })

  const businesses = viewerBusiness?.viewer?.businesses ?? []
  const inviteLinkRequests = viewerBusiness?.viewer?.inviteLinkRequests ?? []

  // Check if error is UNAUTHORIZED
  const error = viewerUserError || viewerBusinessesError
  const isUnauthorized = error && 'graphQLErrors' in error &&
    error.graphQLErrors.some(e => e.extensions?.code === 'UNAUTHORIZED')

  // Show login form for unauthorized errors
  if (isUnauthorized) {
    return <HomeLogin resetError={() => {
      if (viewerBusinessesError) {
        refetchviewerBusinesses()
      }
    }} />
  }

  return (
    <>
      <Dialog open={open} onClose={handleDialogClose}>
        <DialogTitle>{t("Join")}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t("Please enter your name.")}</DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            id="name"
            label={t("Name")}
            type="text"
            fullWidth
            value={name}
            onChange={handleNameChange}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>{t("Cancel")}</Button>
          <Button onClick={handleDialogSubmit}>{t("Submit")}</Button>
        </DialogActions>
      </Dialog>
      <HomeAppBar>
        <AccountButton
          alt={""}
          fileId={null}
          onClick={() => {
            router.push("/account")
          }}
        />
      </HomeAppBar>
      {error ? (
        <ErrorDisplay
          error={error}
          onRetry={() => {
            if (viewerBusinessesError) {
              refetchviewerBusinesses()
            }
          }}
        />
      ) : viewerBusinessesLoading ? (
        <RootLoading />
      ) : (
        <Stack direction={"row"} justifyContent={"center"}>
          <Stack
            sx={{ maxWidth: 800, width: "100%", px: 2, mt: 2, mb: 2 }}
            spacing={4}
          >
            <Stack
              direction={"row"}
              justifyContent={"space-between"}
              spacing={1}
            >
              <Typography sx={{ fontSize: 20, fontWeight: "bold" }}>
                {t("Your Businesses")}
              </Typography>
              {viewer?.viewer?.user.stripeCustomerId && (
                <AddButton
                  onClick={() => {
                    router.push("/businesses/new")
                  }}
                  text={t("Add Business")}
                />
              )}
            </Stack>
            {invitationCode && (
              <InviteLinkInfo
                invitationCode={invitationCode}
                onJoin={handleJoin}
                onClose={handleClose}
              />
            )}

            <Box>
              <Grid container spacing={2}>
                {businesses.map((business) => (
                  <Grid xs={12} sm={6} key={business.id}>
                    <BusinessCard
                      businessCompanyName={business.companyName ?? "-"}
                      businessLogin={business.login}
                      waitingForApproval={false}
                      businessName={business.name}
                      onClick={() => router.push(`/${business.login}`)}
                    />
                  </Grid>
                ))}
                {inviteLinkRequests.map((inviteLinkRequest) => (
                  <Grid xs={12} sm={6} key={inviteLinkRequest.id}>
                    <BusinessCard
                      businessCompanyName={
                        inviteLinkRequest.business.companyName ?? ""
                      }
                      waitingForApproval={true}
                      businessLogin={inviteLinkRequest.business.login}
                      businessName={inviteLinkRequest.business.name}
                      onClick={() => {}}
                    />
                  </Grid>
                ))}
                {businesses.length == 0 && (
                  <Grid xs={12} sm={6}>
                    <BusinessNewCard
                      onClick={() => {
                        router.push("/businesses/new")
                      }}
                    />
                  </Grid>
                )}
              </Grid>
            </Box>
          </Stack>
        </Stack>
      )}
    </>
  )
}

RootPage.getLayout = (page) => {
  return <>{page}</>
}

export default RootPage
